import React, { useEffect, useState, useRef } from "react";
import { Section, ToolbarButton } from "./UI";
import { leadTypes, trafficSources, commissionModels, LEAD_STATUSES, LEAD_PRIORITIES, CONTACT_TYPES } from "../constants/leads";
import { FormInput } from "./FormInput";
import { FormSelect } from "./FormSelect";
import { MultiSelect } from "./MultiSelect";
import { PhotoUpload } from "./PhotoUpload";
import { VoiceNoteRecorder } from "./VoiceNoteRecorder";
import { SimpleVoiceRecorder } from "./SimpleVoiceRecorder";
import { AINotesButton } from "./AINotesButton";
import { NotesEditor } from "./NotesEditor";
import { DateTimePicker } from "./DateTimePicker";
import { INPUT_BASE, LABEL_TEXT, TEXTAREA_BASE } from "../styles/classNames";
import { eventsService } from "../services/eventsService";

export function LeadForm({ mode, form, onChange, handleSave, resetForm, setView, handlePhotos, removePhoto, loading, handleSaveAndNew, handleSaveAndAI, handleVoiceNoteUpload, handleVoiceNoteDelete }) {
  const isEdit = mode === "edit";
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const contactTextareaRef = useRef(null);
  
  /**
   * Обработчик транскрибации аудио
   */
  const handleTranscriptionComplete = (transcribedText) => {
    console.log('[LeadForm] Транскрипция получена:', transcribedText);
    
    // Добавляем транскрипцию к существующим заметкам через \n\n
    const currentNotes = form.notes || '';
    const newNotes = currentNotes.trim() 
      ? currentNotes + '\n\n' + transcribedText 
      : transcribedText;
    
    onChange('notes', newNotes);
  };

  // Load events from API
  useEffect(() => {
    const loadEvents = async () => {
      try {
        setLoadingEvents(true);
        console.log('[LeadForm] Loading events...');
        const eventsData = await eventsService.getEvents();
        console.log('[LeadForm] Received events:', eventsData);
        // Filter only active events and sort by display_order (0 or 1 first, then 999)
        const activeEvents = eventsData
          .filter(e => e.is_active)
          .sort((a, b) => a.display_order - b.display_order);
        console.log('[LeadForm] Active events (sorted):', activeEvents);
        setEvents(activeEvents);
      } catch (err) {
        console.error("[LeadForm] Failed to load events:", err);
        setEvents([]);
      } finally {
        setLoadingEvents(false);
      }
    };
    loadEvents();
  }, []);

  // Auto-resize contact textarea when value changes
  useEffect(() => {
    if (contactTextareaRef.current) {
      contactTextareaRef.current.style.height = 'auto';
      contactTextareaRef.current.style.height = contactTextareaRef.current.scrollHeight + 'px';
    }
  }, [form.contact]);

  const showCustomEvent = !loadingEvents && (form.event === "Другое" || form.event === "Other" || (form.event && events.length > 0 && !events.some((e) => e.name === form.event)));
  const showCustomType = form.leadType === "Other";
  const showCustomTraffic = Array.isArray(form.trafficSource) && form.trafficSource.includes("Other");
  const showCustomCommission = form.commissionModel === "Other";

  return (
    <Section
      title={isEdit ? "Редактировать лида" : "Добавить лида"}
      right={
        <div className="flex gap-2">
          <ToolbarButton onClick={() => { resetForm(); setView("home"); }} disabled={loading}>Назад</ToolbarButton>
          <ToolbarButton active onClick={handleSave} disabled={loading}>
            {loading ? "Сохранение..." : isEdit ? "Сохранить" : "Сохранить"}
          </ToolbarButton>
        </div>
      }
    >
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <FormSelect
            label="Ивент"
            value={form.event || ""}
            onChange={(e) => onChange("event", e.target.value)}
            options={[
              ...(loadingEvents ? [] : events.map(ev => ({ value: ev.name, label: ev.name }))),
              { value: "Другое", label: "Другое" }
            ]}
            placeholder={loadingEvents ? "Загрузка событий..." : undefined}
            disabled={loadingEvents}
          />
          
          {showCustomEvent && (
            <FormInput
              label="Название ивента"
              value={form.customEvent}
              onChange={(e) => onChange("customEvent", e.target.value)}
              placeholder="Например: iGB Amsterdam 2025"
            />
          )}

          <FormInput
            label="Имя"
            value={form.name}
            onChange={(e) => onChange("name", e.target.value)}
            placeholder="John Doe"
          />

          <FormInput
            label="Company / Team"
            value={form.company}
            onChange={(e) => onChange("company", e.target.value)}
            placeholder="ClickMedia / Team Alpha"
          />

          <label className="block">
            <div className={LABEL_TEXT}>Контакт</div>
            <textarea
              ref={contactTextareaRef}
              value={form.contact}
              onChange={(e) => {
                onChange("contact", e.target.value);
                // Auto-resize
                e.target.style.height = 'auto';
                e.target.style.height = e.target.scrollHeight + 'px';
              }}
              onInput={(e) => {
                // Auto-resize on input
                e.target.style.height = 'auto';
                e.target.style.height = e.target.scrollHeight + 'px';
              }}
              placeholder="@username, email, телефон, сайт"
              className={`${INPUT_BASE} resize-none overflow-hidden`}
              rows={1}
              style={{ minHeight: '42px' }}
            />
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FormSelect
              label="Тип лида"
              value={form.leadType}
              onChange={(e) => onChange("leadType", e.target.value)}
              options={leadTypes}
            />
            {showCustomType && (
              <FormInput
                label="Свой тип"
                value={form.customLeadType}
                onChange={(e) => onChange("customLeadType", e.target.value)}
                placeholder="Например: Podcast host"
              />
            )}
          </div>

          <div className="grid grid-cols-1 gap-3">
            <MultiSelect
              label="Источник трафика (можно выбрать несколько)"
              options={trafficSources.map(source => ({ value: source, label: source }))}
              value={Array.isArray(form.trafficSource) ? form.trafficSource : []}
              onChange={(newValue) => onChange("trafficSource", newValue)}
              placeholder="Выберите источники"
            />
            {showCustomTraffic && (
              <FormInput
                label="Свой источник"
                value={form.customTraffic}
                onChange={(e) => onChange("customTraffic", e.target.value)}
                placeholder="Например: Reddit Ads"
              />
            )}
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600 dark:text-gray-400">Работает с нами?</span>
            <div className="flex gap-2">
              <ToolbarButton active={form.worksWithUs === "yes"} onClick={() => onChange("worksWithUs", "yes")}>Да</ToolbarButton>
              <ToolbarButton active={form.worksWithUs === "no"} onClick={() => onChange("worksWithUs", "no")}>Нет</ToolbarButton>
              <ToolbarButton active={form.worksWithUs === "unknown"} onClick={() => onChange("worksWithUs", "unknown")}>Не уверен</ToolbarButton>
            </div>
          </div>

          <FormSelect
            label="Модель комиссии (обязательно)"
            value={form.commissionModel}
            onChange={(e) => onChange("commissionModel", e.target.value)}
            options={commissionModels}
            placeholder="Не выбрано"
          />
          {showCustomCommission && (
            <FormInput
              label="Своё описание модели"
              value={form.customCommission}
              onChange={(e) => onChange("customCommission", e.target.value)}
              placeholder="Например: гибрид RS+CPA, фикс + %"
            />
          )}

          {/* Статус и Приоритет */}
          <div className="grid grid-cols-2 gap-3">
            <FormSelect
              label="Статус лида"
              value={form.status || "to_process"}
              onChange={(e) => onChange("status", e.target.value)}
              options={LEAD_STATUSES.map(s => ({ value: s.value, label: s.label }))}
            />
            <FormSelect
              label="Приоритет"
              value={form.priority || ""}
              onChange={(e) => onChange("priority", e.target.value || null)}
              options={LEAD_PRIORITIES.map(p => ({ value: p.value, label: p.label }))}
              placeholder="Не указан"
            />
          </div>

          {/* Partner ID - показываем только если статус moved_to_affiliate */}
          {form.status === "moved_to_affiliate" && (
            <FormInput
              label="Partner ID"
              value={form.partner_id || ""}
              onChange={(e) => onChange("partner_id", e.target.value)}
              placeholder="ID партнера в аффилиатской платформе"
            />
          )}

          {/* Контактные данные */}
          <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
            <div className={`${LABEL_TEXT} font-medium`}>Контактные данные</div>
            <div className="grid grid-cols-1 gap-2">
              <FormInput
                label="Telegram"
                value={form.contact_telegram || ""}
                onChange={(e) => onChange("contact_telegram", e.target.value)}
                placeholder="@username или ссылка"
              />
              <FormInput
                label="WhatsApp"
                value={form.contact_whatsapp || ""}
                onChange={(e) => onChange("contact_whatsapp", e.target.value)}
                placeholder="+1234567890"
              />
              <FormInput
                label="WeChat"
                value={form.contact_wechat || ""}
                onChange={(e) => onChange("contact_wechat", e.target.value)}
                placeholder="WeChat ID"
              />
              <FormInput
                label="Email"
                value={form.contact_email || ""}
                onChange={(e) => onChange("contact_email", e.target.value)}
                placeholder="email@example.com"
              />
              <FormInput
                label="Phone"
                value={form.contact_phone || ""}
                onChange={(e) => onChange("contact_phone", e.target.value)}
                placeholder="+1234567890"
              />
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <PhotoUpload
            photos={form.photoDataUrls || []}
            onUpload={handlePhotos}
            onRemove={removePhoto}
            maxPhotos={3}
          />

          {/* Заметки */}
          <div className="space-y-2">
            <div className={`${LABEL_TEXT} flex items-center justify-between`}>
              <span>Заметки</span>
              {/* Голосовой рекордер (без сохранения) */}
              <SimpleVoiceRecorder
                onTranscriptionComplete={handleTranscriptionComplete}
                disabled={loading}
              />
            </div>
            
            {/* AI обработка (вне label, чтобы избежать случайных кликов) */}
            <div className="flex items-center gap-2">
              <AINotesButton
                notes={form.notes}
                onNotesChange={(newNotes) => onChange('notes', newNotes)}
                onNotificationExtracted={(notification) => {
                  // Автоматически заполняем поля напоминания из AI
                  console.log('[LeadForm] Получена notification из AI:', notification);
                  
                  // Включаем чекбокс
                  onChange('notificationEnabled', true);
                  
                  // Формируем ISO datetime из date + time
                  const dateTimeString = `${notification.date}T${notification.time}:00Z`;
                  onChange('notificationDate', dateTimeString);
                  
                  // Устанавливаем текст события
                  onChange('notificationMessage', notification.event);
                }}
                disabled={loading}
              />
            </div>
            
            {/* Редактор заметок с Markdown */}
            <NotesEditor
              value={form.notes}
              onChange={(newNotes) => onChange('notes', newNotes)}
              disabled={loading}
            />
          </div>

          {/* Notification section */}
          <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.notificationEnabled || false}
                onChange={(e) => {
                  onChange('notificationEnabled', e.target.checked);
                  if (!e.target.checked) {
                    // Очищаем данные при выключении
                    onChange('notificationDate', null);
                    onChange('notificationMessage', '');
                  }
                }}
                className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-blue-500 focus:ring-blue-500 focus:ring-2"
                disabled={loading}
              />
              <span className={`${LABEL_TEXT} !mb-0 font-medium`}>🔔 Напоминание</span>
            </label>

            {form.notificationEnabled && (
              <div className="space-y-3 pl-8">
                <div>
                  <div className={LABEL_TEXT}>Дата и время напоминания *</div>
                  <DateTimePicker
                    value={form.notificationDate}
                    onChange={(value) => onChange('notificationDate', value)}
                    disabled={loading}
                  />
                </div>

                <div>
                  <div className={LABEL_TEXT}>Текст напоминания</div>
                  <textarea
                    value={form.notificationMessage || ''}
                    onChange={(e) => onChange('notificationMessage', e.target.value)}
                    placeholder="Например: Перезвонить по поводу партнерства"
                    className={`${INPUT_BASE} min-h-[80px] resize-none`}
                    disabled={loading}
                    rows={3}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Кнопки "Сохранить ещё" и "Сохранить и AI" только для режима добавления */}
      {!isEdit && (handleSaveAndNew || handleSaveAndAI) && (
        <div className="mt-4 flex justify-center gap-3 flex-wrap">
          {handleSaveAndNew && (
            <ToolbarButton 
              onClick={handleSaveAndNew} 
              disabled={loading}
              className="px-6"
            >
              {loading ? "Сохранение..." : "Сохранить + Новый лид"}
            </ToolbarButton>
          )}
          {handleSaveAndAI && (
            <ToolbarButton 
              onClick={handleSaveAndAI} 
              disabled={loading}
              className="px-6 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400"
            >
              {loading ? "Сохранение..." : "🤖 Сохранить + AI скан"}
            </ToolbarButton>
          )}
        </div>
      )}
    </Section>
  );
}
